import React, { useState, useMemo } from 'react';
import { Calendar, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { RedactionText } from '../../redaction/RedactionText';
import type { ChronologyTimelineWidgetData, ChronologyTimelineEvent } from '../../../types/agentWidgets';
import { useDocumentViewer } from '../../../contexts/DocumentViewerContext';
import { dotColourIndex, dotColourClasses } from '../../citations/CitationSuperscript';

interface Props {
  data: ChronologyTimelineWidgetData;
}

const EVENT_TYPE_COLORS: Record<string, string> = {
  ADMISSION: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  DISCHARGE: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  DIAGNOSIS: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  MAJOR_PROCEDURE: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  SURGICAL_EPISODE: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  TREATMENT_CHANGE: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  TREATMENT_COURSE: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  CLINICAL_DETERIORATION: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  CLINICAL_IMPROVEMENT: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  SIGNIFICANT_INVESTIGATION: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  CONSULTATION: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  OUTPATIENT_APPOINTMENT: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  HOSPITAL_VISIT: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  EMERGENCY_VISIT: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  MEDICATION_ROUTINE: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  VITAL_SIGNS: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  LAB_RESULT: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  OTHER: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const SIGNIFICANCE_COLORS: Record<string, string> = {
  MAJOR: 'bg-red-500',
  SUPPORTING: 'bg-amber-400',
  ROUTINE: 'bg-gray-300 dark:bg-gray-600',
};

type FilterTab = 'all' | 'MAJOR' | 'SUPPORTING';

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Unknown date';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatEventType(type: string): string {
  return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

const INITIAL_DISPLAY = 5;

const TimelineEvent: React.FC<{ event: ChronologyTimelineEvent }> = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const { openDocument } = useDocumentViewer();

  const typeColor = EVENT_TYPE_COLORS[event.eventType] || EVENT_TYPE_COLORS.OTHER;
  const sigColor = SIGNIFICANCE_COLORS[event.significance] || SIGNIFICANCE_COLORS.ROUTINE;
  const lowConfidence = event.confidence != null && event.confidence < 0.8;

  return (
    <div className="flex gap-3 group">
      {/* Date column */}
      <div className="w-[85px] flex-shrink-0 text-right pt-0.5">
        <span className="text-[11px] text-gray-500 dark:text-gray-400 font-mono whitespace-nowrap">
          {formatDate(event.eventDate)}
        </span>
      </div>

      {/* Timeline dot + line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${sigColor} flex-shrink-0`} />
        <div className="w-px flex-1 bg-gray-200 dark:bg-medical-600" />
      </div>

      {/* Card */}
      <div className="flex-1 min-w-0 pb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${typeColor}`}>
            {formatEventType(event.eventType)}
          </span>
          {lowConfidence && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400" title={`Confidence: ${Math.round((event.confidence ?? 0) * 100)}%`}>
              <AlertTriangle className="h-2.5 w-2.5" />
              Low confidence
            </span>
          )}
        </div>

        <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mt-0.5 leading-snug">
          <RedactionText text={event.headline} />
        </p>

        {event.summary && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mt-0.5 text-left"
          >
            <RedactionText text={expanded ? event.summary : (event.summary.length > 150 ? event.summary.substring(0, 147) + '...' : event.summary)} />
          </button>
        )}

        {/* Citations — compact dots matching prose citation style */}
        {event.citations && event.citations.length > 0 && (
          <div className="flex items-center gap-0.5 mt-0.5">
            {event.citations.map((c, i) => {
              const colourIdx = dotColourIndex(c.documentId || '');
              const tooltip = `${c.documentName}${c.pageNumber != null ? `, p.${c.pageNumber}` : ''}`;
              return (
                <sup
                  key={i}
                  onClick={() => c.documentId && openDocument({ documentId: c.documentId, documentName: c.documentName, pageNumber: c.pageNumber })}
                  title={tooltip}
                  className={`text-[10px] leading-none font-bold cursor-pointer select-none transition-colors ${dotColourClasses(colourIdx)}`}
                >
                  •
                </sup>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const ChronologyTimelineWidget: React.FC<Props> = ({ data }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [showAll, setShowAll] = useState(false);

  const filteredEvents = useMemo(() => {
    if (filter === 'all') return data.events;
    return data.events.filter(e => e.significance === filter);
  }, [data.events, filter]);

  const displayEvents = showAll ? filteredEvents : filteredEvents.slice(0, INITIAL_DISPLAY);
  const hasMore = filteredEvents.length > INITIAL_DISPLAY;

  if (!data.events || data.events.length === 0) return null;

  return (
    <div className="bg-amber-50/30 dark:bg-amber-900/5 border-l-3 border-amber-400 dark:border-amber-600">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-amber-50/50 dark:hover:bg-amber-900/10 transition-colors"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />}
        <Calendar className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
          Timeline — {data.patientName}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          {data.eventCount} events
        </span>
      </button>

      {!collapsed && (
        <div className="px-3 pb-3">
          {/* Filter tabs */}
          <div className="flex gap-1 mb-3">
            {(['all', 'MAJOR', 'SUPPORTING'] as FilterTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => { setFilter(tab); setShowAll(false); }}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                  filter === tab
                    ? 'bg-amber-200 text-amber-800 dark:bg-amber-800/40 dark:text-amber-300'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-medical-700'
                }`}
              >
                {tab === 'all' ? 'All' : tab === 'MAJOR' ? 'Major' : 'Supporting'}
              </button>
            ))}
          </div>

          {/* Timeline events */}
          <div>
            {displayEvents.map((event, i) => (
              <TimelineEvent key={i} event={event} />
            ))}
          </div>

          {/* Show more */}
          {hasMore && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="text-[11px] text-amber-600 dark:text-amber-400 hover:underline mt-1 ml-[97px]"
            >
              Show all {filteredEvents.length} events
            </button>
          )}
        </div>
      )}
    </div>
  );
};
